import {
  Building2,
  Check,
  Factory,
  FlaskConical,
  Laptop,
  MousePointer2,
  Package,
  ScanEye,
  ScanFace,
  Smartphone,
  ShoppingBag,
  Sparkles,
  Truck,
  Users,
} from 'lucide-react';
import { SiShopify } from 'react-icons/si';
import FrameArt from '@/components/ui/FrameArt';
import { HUB, MAP_H, MAP_W, SHIP_ROUTES, US_STATE_PATHS, pct } from './usMap';

/**
 * Markup for the order tour. Pure layout, no motion: `OrderTour` owns the
 * timeline and moves things by the data attributes set here.
 *
 * Everything is drawn in a fixed design space and the stage is scaled to fit,
 * so a position measured here means the same thing on every screen. There are
 * two spaces: 760×480 beside the text on a wide screen, and a portrait 400×500
 * under it on a narrow one. `data-compact` on the stage switches the layout
 * through the `group/stage` variants below; every travelling position is
 * measured, so the timeline does not care which one it is running over.
 *
 * `data-anchor` elements are empty slots of an exact size. The one travelling
 * element — `[data-token]` — is laid over each slot in turn and wears the face
 * that belongs there: a frame in the store, an order row, a prescription, a
 * try-on card, a patient-record line, a lab job, a job tray, a parcel. That token is order
 * #4821, and it is the story.
 *
 * Markup is written in its finished state; the timeline hides what it is going
 * to reveal. Under reduced motion only the last scene is shown, as a still.
 */
export const STAGE_W = 760;
export const STAGE_H = 480;
export const COMPACT_W = 400;
export const COMPACT_H = 500;
export const RAIL_PITCH = 44;

export const ORDER_ID = '#4821';

export const SCENES = [
  { key: 'store', crumb: 'E-Commerce', icon: ShoppingBag },
  { key: 'ai', crumb: 'LenSync AI', icon: ScanEye },
  { key: 'fit', crumb: 'Camera fitting', icon: ScanFace },
  { key: 'pms', crumb: 'Practice Management', icon: Users },
  { key: 'lab', crumb: 'Lab Management', icon: FlaskConical },
  { key: 'edging', crumb: 'Edging', icon: Factory },
  { key: 'ship', crumb: 'Shipping', icon: Truck },
];

const LINE = 'border-secondary-dark/[0.07]';
const WELL = 'bg-[#f5f8fc]';

function OrderChip({ className = '' }) {
  return (
    <span
      className={`shrink-0 rounded bg-primary-blue/10 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-primary-blue ${className}`}
    >
      {ORDER_ID}
    </span>
  );
}

function SceneLabel({ children, right = null }) {
  return (
    <div className="flex h-5 items-center justify-between gap-2">
      <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-secondary-dark/45">
        {children}
      </span>
      {right}
    </div>
  );
}

function Tick({ className = '' }) {
  return (
    <span
      className={`grid size-[18px] shrink-0 place-items-center rounded-full bg-emerald-500 text-white ${className}`}
    >
      <Check className="size-3" strokeWidth={3.5} />
    </span>
  );
}

function Scene({ name, children }) {
  const first = name === SCENES[0].key;
  const last = name === SCENES[SCENES.length - 1].key;
  const state = first
    ? 'motion-reduce:hidden'
    : last
      ? 'invisible opacity-0 motion-reduce:visible motion-reduce:opacity-100'
      : 'invisible opacity-0 motion-reduce:hidden';
  return (
    <div
      data-scene={name}
      className={`absolute inset-0 p-5 group-data-[compact=true]/stage:p-3.5 ${state}`}
    >
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Window chrome + module rail                                        */
/* ------------------------------------------------------------------ */

export function WindowChrome() {
  return (
    <div
      data-chrome
      className={`absolute inset-x-0 top-0 flex h-12 items-center justify-between border-b px-4 ${LINE}`}
    >
      <div className="flex items-center gap-2.5">
        <span className="grid size-7 place-items-center rounded-lg bg-primary-blue text-white">
          <Sparkles className="size-4" strokeWidth={2.5} />
        </span>
        <span className="text-sm font-semibold tracking-tight text-secondary-dark">LenSync</span>
        <span className="text-secondary-dark/20">/</span>
        <span className="relative h-5 w-40 group-data-[compact=true]/stage:w-32">
          {SCENES.map((s, i) => (
            <span
              key={s.key}
              data-crumb={i}
              className={`absolute inset-0 whitespace-nowrap text-sm text-secondary-dark/55 ${i ? 'invisible opacity-0' : ''}`}
            >
              {s.crumb}
            </span>
          ))}
        </span>
      </div>
      <span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-ink px-3 py-1 font-mono text-[11px] font-medium text-white">
        <span className="size-1.5 animate-pulse rounded-full bg-emerald-400" />
        {ORDER_ID}
      </span>
    </div>
  );
}

export function ModuleRail() {
  return (
    <div
      data-rail
      className={`relative w-14 shrink-0 border-r pt-3 group-data-[compact=true]/stage:hidden ${LINE}`}
    >
      <span
        data-nav-pill
        className="absolute left-2.5 top-3 size-9 rounded-xl bg-primary-blue shadow-[0_6px_16px_rgba(1,167,255,0.35)]"
      />
      {SCENES.map((s, i) => {
        const Icon = s.icon;
        return (
          <span
            key={s.key}
            className="relative mx-auto grid size-9 place-items-center text-secondary-dark/35"
            style={{ marginBottom: RAIL_PITCH - 36 }}
          >
            <Icon className="size-[18px]" strokeWidth={2} />
            {/* the same icon in white, shown while the pill sits under it */}
            <span
              data-nav-on={i}
              className={`absolute inset-0 grid place-items-center text-white ${i ? 'invisible opacity-0' : ''}`}
            >
              <Icon className="size-[18px]" strokeWidth={2} />
            </span>
          </span>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  01 · store — a frame is bought, an order appears                   */
/* ------------------------------------------------------------------ */

const FRAMES = [
  {
    name: 'Aviator Classic',
    price: '$129',
    shape: 'aviator',
    tints: ['#c9a25b', '#1f2937', '#9ca3af'],
  },
  { name: 'Round Metal', price: '$98', shape: 'round', tints: ['#1f2937', '#c9a25b', '#9ca3af'] },
  {
    name: 'Square Acetate',
    price: '$145',
    shape: 'square',
    tints: ['#7c4a2d', '#1f2937', '#0f766e'],
  },
];
const CHOSEN_FRAME = 1;

function FrameCard({ name, price, shape, tints, chosen = false, children = null }) {
  return (
    <div
      className={`relative flex h-full flex-col rounded-xl border bg-white p-2 ${chosen ? 'border-primary-blue' : LINE}`}
    >
      <div className="flex min-h-0 flex-1 items-center justify-center rounded-lg bg-gradient-to-br from-[#eef6ff] to-[#f8fbff] text-secondary-dark">
        <FrameArt shape={shape} className="w-[78%]" />
      </div>
      <div className="mt-2 truncate text-[11px] font-semibold text-secondary-dark">{name}</div>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[11px] text-secondary-dark/55">{price}</span>
        <span className="flex shrink-0 gap-0.5">
          {tints.map((t) => (
            <span key={t} className="size-1.5 rounded-full" style={{ background: t }} />
          ))}
        </span>
      </div>
      {children}
    </div>
  );
}

const QUEUE = [
  { id: '#4820', item: 'Aviator Classic · Single vision' },
  { id: '#4819', item: 'Square Acetate · Blue light' },
];

function QueueRow({ id, item, status, tone = 'text-secondary-dark/45', ...rest }) {
  return (
    <div
      className={`flex h-[52px] items-center gap-2.5 rounded-xl border bg-white px-3 ${LINE}`}
      {...rest}
    >
      <span className="shrink-0 font-mono text-[11px] font-semibold text-secondary-dark/45">
        {id}
      </span>
      <span className="min-w-0 flex-1 truncate text-xs text-secondary-dark/60">{item}</span>
      <span className={`shrink-0 font-mono text-[10px] uppercase tracking-[0.08em] ${tone}`}>
        {status}
      </span>
    </div>
  );
}

export function StoreScene() {
  return (
    <Scene name="store">
      <div className="flex h-full gap-4 group-data-[compact=true]/stage:flex-col group-data-[compact=true]/stage:gap-3">
        <div className="flex min-w-0 flex-1 flex-col">
          <SceneLabel
            right={
              <span className="inline-flex items-center gap-1 font-mono text-[10px] font-semibold text-[#008060]">
                <SiShopify className="text-xs" />
                Shopify
              </span>
            }
          >
            Your storefront
          </SceneLabel>
          <div className="mt-3 grid grid-cols-3 gap-2.5">
            {FRAMES.map((f, i) => (
              <div key={f.name} className="h-[214px] group-data-[compact=true]/stage:h-[120px]">
                {i === CHOSEN_FRAME ? (
                  <div data-anchor="frame" className={`h-full rounded-xl ${WELL}`} />
                ) : (
                  <FrameCard {...f} />
                )}
              </div>
            ))}
          </div>
          <div
            data-cart-note
            className="mt-3 flex items-center gap-2 text-xs text-secondary-dark/60"
          >
            <Tick />
            Checkout complete — order created in LenSync
          </div>
          <div
            className={`mt-auto flex items-center gap-3 rounded-xl border px-3 py-2.5 group-data-[compact=true]/stage:hidden ${LINE}`}
          >
            <ShoppingBag className="size-4 shrink-0 text-secondary-dark/40" strokeWidth={2} />
            <span className="min-w-0 flex-1 truncate text-xs text-secondary-dark/60">
              Round Metal · Progressive lenses
            </span>
            <span className="font-mono text-xs font-semibold text-secondary-dark">$98.00</span>
            <span className="rounded-full bg-ink px-3 py-1 text-[11px] font-semibold text-white">
              Checkout
            </span>
          </div>
        </div>

        <div
          className={`w-[310px] shrink-0 rounded-2xl p-3 group-data-[compact=true]/stage:w-full ${WELL}`}
        >
          <div className="flex h-4 items-center justify-between">
            <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-secondary-dark/45">
              New orders
            </span>
            <span className="relative grid h-5 w-6 place-items-center rounded-full bg-white font-mono text-[11px] font-semibold text-secondary-dark">
              <span data-count="from" className="invisible absolute opacity-0">
                2
              </span>
              <span data-count="to">3</span>
            </span>
          </div>
          <div data-anchor="order" className="mt-2.5 h-[52px] rounded-xl" />
          {QUEUE.map((r) => (
            <div key={r.id} data-queue-row className="mt-2">
              <QueueRow {...r} status="Processed" />
            </div>
          ))}
        </div>
      </div>
    </Scene>
  );
}

/* ------------------------------------------------------------------ */
/*  02 · ai — the prescription is read                                 */
/* ------------------------------------------------------------------ */

// What the doctor wrote, and what LenSync reads off it.
const RX_COLS = ['SPH', 'CYL', 'AXIS', 'ADD'];
const RX_ROWS = [
  ['OD', '−2.25', '−0.50', '180', '+1.50'],
  ['OS', '−2.00', '−0.75', '170', '+1.50'],
];

export function AiScene() {
  return (
    <Scene name="ai">
      <div className="flex h-full gap-4 group-data-[compact=true]/stage:flex-col group-data-[compact=true]/stage:gap-3">
        <div className="flex w-[290px] shrink-0 flex-col group-data-[compact=true]/stage:w-full">
          <SceneLabel>Uploaded prescription</SceneLabel>
          <div
            data-anchor="rx"
            className="mt-3 flex-1 rounded-xl group-data-[compact=true]/stage:h-[150px] group-data-[compact=true]/stage:flex-none"
          />
        </div>

        <div className={`flex min-w-0 flex-1 flex-col rounded-2xl p-4 ${WELL}`}>
          <SceneLabel
            right={
              <span className="inline-flex items-center gap-1 font-mono text-[10px] font-semibold text-primary-blue">
                <Sparkles className="size-3" strokeWidth={2.5} />
                LenSync AI
              </span>
            }
          >
            Structured order
          </SceneLabel>

          <div className="mt-3 space-y-2">
            {RX_ROWS.map(([eye, ...vals]) => (
              <div key={eye} data-field className="flex items-stretch gap-1.5">
                <span className="grid w-8 shrink-0 place-items-center rounded-lg bg-primary-blue font-mono text-[11px] font-semibold text-white">
                  {eye}
                </span>
                {vals.map((v, i) => (
                  <span
                    key={RX_COLS[i]}
                    className={`min-w-0 flex-1 rounded-lg border bg-white px-2 py-1.5 ${LINE}`}
                  >
                    <span className="block font-mono text-[8.5px] tracking-[0.1em] text-secondary-dark/40">
                      {RX_COLS[i]}
                    </span>
                    <span data-value className="block font-mono text-[12px] text-secondary-dark">
                      {v}
                    </span>
                  </span>
                ))}
              </div>
            ))}
            <div data-field className="flex gap-1.5">
              {[
                ['Lens', 'Progressive'],
                ['Index', '1.67'],
              ].map(([k, v]) => (
                <span
                  key={k}
                  className={`min-w-0 flex-1 rounded-lg border bg-white px-2 py-1.5 ${LINE}`}
                >
                  <span className="block font-mono text-[8.5px] uppercase tracking-[0.1em] text-secondary-dark/40">
                    {k}
                  </span>
                  <span
                    data-value
                    className="block truncate font-mono text-[12px] text-secondary-dark"
                  >
                    {v}
                  </span>
                </span>
              ))}
              {/* not on the prescription: the next chapter measures it */}
              <span className="min-w-0 flex-1 rounded-lg border border-dashed border-amber-400/70 bg-amber-50/60 px-2 py-1.5">
                <span className="block font-mono text-[8.5px] uppercase tracking-[0.1em] text-amber-700/70">
                  PD · Seg ht
                </span>
                <span className="block truncate font-mono text-[12px] text-amber-700">
                  To measure
                </span>
              </span>
            </div>
          </div>

          <div className="mt-auto flex items-center gap-2 pt-3">
            <span
              data-validated
              className="inline-flex items-center gap-2 text-xs font-medium text-secondary-dark"
            >
              <Tick />
              Validated — no errors found
            </span>
          </div>
        </div>
      </div>
    </Scene>
  );
}

/* ------------------------------------------------------------------ */
/*  03 · fit — the camera tries the frame on and takes the measurements */
/* ------------------------------------------------------------------ */

// Where the camera finds the face. Pupils sit at (160,165) and (240,165).
const MESH = [
  [200, 44],
  [150, 56],
  [250, 56],
  [116, 104],
  [284, 104],
  [102, 165],
  [298, 165],
  [110, 226],
  [290, 226],
  [140, 282],
  [260, 282],
  [200, 318],
  [200, 118],
  [200, 212],
  [178, 252],
  [222, 252],
  [140, 146],
  [180, 144],
  [220, 144],
  [260, 146],
];
const MESH_LINKS = [
  [0, 1],
  [0, 2],
  [1, 3],
  [2, 4],
  [3, 5],
  [4, 6],
  [5, 7],
  [6, 8],
  [7, 9],
  [8, 10],
  [9, 11],
  [10, 11],
  [12, 13],
  [13, 14],
  [13, 15],
  [16, 17],
  [18, 19],
  [12, 17],
  [12, 18],
];
const LENS_R = 27;
const LENS_Y = 167;
const PUPIL_Y = 165;
const SEG_X = 292;

function CameraView() {
  return (
    <div className="relative h-full overflow-hidden rounded-2xl bg-ink">
      <div className="absolute inset-0 bg-[radial-gradient(70%_60%_at_50%_45%,rgba(1,167,255,0.22),transparent)]" />
      <svg
        viewBox="0 0 400 360"
        fill="none"
        className="absolute inset-0 h-full w-full"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* the customer, as the camera outlines them */}
        <path
          d="M101 158C92 78 140 26 200 26s108 52 99 132c-8-38-24-62-46-72-30 14-76 14-106 0-22 10-38 34-46 72Z"
          fill="#fff"
          fillOpacity="0.09"
        />
        <g stroke="#fff" strokeOpacity="0.4" strokeWidth="1.5">
          <path d="M200 40c70 0 100 55 100 125 0 85-45 155-100 155s-100-70-100-155c0-70 30-125 100-125Z" />
          <path d="M100 150c-9 2-12 14-9 27s9 21 16 20M300 150c9 2 12 14 9 27s-9 21-16 20" />
          <path d="M166 312c-2 22-18 34-60 48M234 312c2 22 18 34 60 48" />
          <path d="M142 165q18-13 36 0-18 11-36 0ZM222 165q18-13 36 0-18 11-36 0Z" />
          <path d="M140 146q20-10 40-2M220 144q20-8 40 2" />
          <path d="M200 168l-6 44q6 6 14 0" />
          <path d="M178 252q22 12 44 0" />
        </g>

        {/* face mesh */}
        <g stroke="#01a7ff" strokeOpacity="0.35" strokeWidth="0.75">
          {MESH_LINKS.map(([a, b]) => (
            <path
              key={`${a}-${b}`}
              data-mesh
              d={`M${MESH[a][0]} ${MESH[a][1]}L${MESH[b][0]} ${MESH[b][1]}`}
            />
          ))}
        </g>
        <g fill="#01a7ff">
          {MESH.map(([x, y]) => (
            <circle key={`${x}-${y}`} data-mesh cx={x} cy={y} r="2" />
          ))}
        </g>

        {/* the frame, tried on */}
        <g data-glasses stroke="#fff" strokeWidth="3.5">
          <circle cx="160" cy={LENS_Y} r={LENS_R} fill="#fff" fillOpacity="0.06" />
          <circle cx="240" cy={LENS_Y} r={LENS_R} fill="#fff" fillOpacity="0.06" />
          <path d="M187 162q13-9 26 0M133 161l-31-5M267 161l31-5" />
        </g>

        {/* pupils */}
        {[160, 240].map((x) => (
          <g key={x} data-pupil stroke="#01a7ff" strokeWidth="1.5">
            <circle cx={x} cy={PUPIL_Y} r="6" fill="#01a7ff" fillOpacity="0.35" />
            <path d={`M${x - 12} ${PUPIL_Y}H${x + 12}M${x} ${PUPIL_Y - 12}V${PUPIL_Y + 12}`} />
          </g>
        ))}

        {/* PD: pupil to pupil */}
        <g data-pd>
          <path
            d={`M160 ${PUPIL_Y - 16}V104M240 ${PUPIL_Y - 16}V104`}
            stroke="#01a7ff"
            strokeOpacity="0.6"
            strokeWidth="1"
            strokeDasharray="2 4"
          />
          <path d="M160 110H240M160 104v12M240 104v12" stroke="#01a7ff" strokeWidth="2" />
          <rect x="160" y="78" width="80" height="20" rx="10" fill="#01a7ff" />
          <text
            x="200"
            y="92"
            textAnchor="middle"
            fill="#fff"
            fontSize="11"
            fontWeight="600"
            fontFamily="var(--font-mono), monospace"
          >
            PD 63.0
          </text>
        </g>

        {/* segment height: pupil centre down to the bottom of the lens */}
        <g data-seg>
          <path
            d={`M252 ${PUPIL_Y}H${SEG_X + 6}M258 ${LENS_Y + LENS_R}H${SEG_X + 6}`}
            stroke="#34d399"
            strokeOpacity="0.7"
            strokeWidth="1"
            strokeDasharray="2 4"
          />
          <path
            d={`M${SEG_X} ${PUPIL_Y}V${LENS_Y + LENS_R}M${SEG_X - 6} ${PUPIL_Y}h12M${SEG_X - 6} ${LENS_Y + LENS_R}h12`}
            stroke="#34d399"
            strokeWidth="2"
          />
          <rect x={SEG_X + 10} y="172" width="84" height="20" rx="10" fill="#34d399" />
          <text
            x={SEG_X + 52}
            y="186"
            textAnchor="middle"
            fill="#0a1220"
            fontSize="11"
            fontWeight="600"
            fontFamily="var(--font-mono), monospace"
          >
            SEG 18.5
          </text>
        </g>

        {/* viewfinder corners */}
        <path
          d="M20 46V20h26M354 20h26v26M380 314v26h-26M46 340H20v-26"
          stroke="#fff"
          strokeOpacity="0.5"
          strokeWidth="2"
        />
      </svg>

      <div
        data-cam-scan
        className="pointer-events-none absolute inset-x-0 h-16"
        style={{ top: '-20%' }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-primary-blue/0 to-primary-blue/25" />
        <div className="absolute inset-x-0 bottom-0 h-px bg-primary-blue shadow-[0_0_14px_2px_rgba(1,167,255,0.9)]" />
      </div>

      <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.1em] text-white backdrop-blur">
        <span className="size-1.5 animate-pulse rounded-full bg-rose-500" />
        Camera
      </span>
    </div>
  );
}

export function FitScene() {
  return (
    <Scene name="fit">
      <div className="flex h-full gap-4 group-data-[compact=true]/stage:flex-col group-data-[compact=true]/stage:gap-3">
        <div className="min-h-0 min-w-0 flex-1 group-data-[compact=true]/stage:h-[214px] group-data-[compact=true]/stage:flex-none">
          <CameraView />
        </div>

        <div className="flex w-[262px] shrink-0 flex-col group-data-[compact=true]/stage:w-full">
          <SceneLabel>Try-on &amp; measurements</SceneLabel>
          <div data-anchor="fit" className={`mt-3 h-[60px] rounded-xl ${WELL}`} />

          <div className="mt-3 grid grid-cols-3 gap-1.5">
            {[
              ['PD', '63.0'],
              ['Seg OD', '18.5'],
              ['Seg OS', '18.5'],
            ].map(([k, v]) => (
              <span
                key={k}
                data-fit-cell
                className={`rounded-lg border bg-white px-2 py-1.5 ${LINE}`}
              >
                <span className="block font-mono text-[8.5px] uppercase tracking-[0.1em] text-secondary-dark/40">
                  {k}
                </span>
                <span className="flex items-baseline gap-1 font-mono text-[13px] text-secondary-dark">
                  <span data-fit-value>{v}</span>
                  <span className="text-[9px] text-secondary-dark/40">mm</span>
                </span>
              </span>
            ))}
          </div>

          <p
            data-fit-note
            className="mt-3 flex items-center gap-2 text-xs text-secondary-dark/70 group-data-[compact=true]/stage:hidden"
          >
            <Tick />
            Measured on camera — no ruler needed
          </p>

          <div
            className={`mt-auto flex gap-1 rounded-xl p-1 text-[11px] font-medium group-data-[compact=true]/stage:hidden ${WELL}`}
          >
            <span className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-white py-1.5 font-semibold text-primary-blue shadow-sm">
              <Laptop className="size-3.5" strokeWidth={2} />
              Laptop
            </span>
            <span className="flex flex-1 items-center justify-center gap-1.5 py-1.5 text-secondary-dark/50">
              <Smartphone className="size-3.5" strokeWidth={2} />
              Phone
            </span>
          </div>
        </div>
      </div>
    </Scene>
  );
}

/* ------------------------------------------------------------------ */
/*  04 · pms — the order joins the patient record                      */
/* ------------------------------------------------------------------ */

const HISTORY = [
  { id: '#4512', item: 'Aviator Classic · Single vision' },
  { id: '#4108', item: 'Square Acetate · Blue light' },
];
const VIEWERS = [
  { id: 'AB', c: 'from-sky-400 to-sky-600' },
  { id: 'JD', c: 'from-emerald-400 to-emerald-600' },
  { id: 'MK', c: 'from-slate-500 to-slate-700' },
];

export function PmsScene() {
  return (
    <Scene name="pms">
      <div className="flex h-full flex-col">
        <div className="flex items-center gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-full bg-gradient-to-br from-primary-blue to-[#0077c8] text-xs font-bold text-white">
            EC
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-secondary-dark">Emma Collins</div>
            <div className="font-mono text-[10px] text-secondary-dark/45">
              MRN 00417 · Patient record
            </div>
          </div>
          <div
            className={`flex shrink-0 gap-1 rounded-full p-1 text-[11px] font-medium text-secondary-dark/50 ${WELL}`}
          >
            <span className="px-2.5 py-1 group-data-[compact=true]/stage:hidden">Exams</span>
            <span className="px-2.5 py-1">Rx</span>
            <span className="rounded-full bg-white px-2.5 py-1 font-semibold text-primary-blue shadow-sm">
              Orders
            </span>
          </div>
        </div>

        <div className={`mt-3 flex flex-1 flex-col rounded-2xl p-3 ${WELL}`}>
          <div data-anchor="record" className="h-[52px] rounded-xl" />
          {HISTORY.map((r) => (
            <div key={r.id} data-old-row className="mt-2 opacity-70">
              <QueueRow {...r} status="Delivered" tone="text-emerald-600" />
            </div>
          ))}

          <div className="mt-auto flex items-center justify-between gap-3 pt-3">
            <div className="flex items-center">
              <div className="flex">
                {VIEWERS.map((p) => (
                  <span
                    key={p.id}
                    data-viewer
                    className={`-ml-2 grid size-8 place-items-center rounded-full bg-gradient-to-br ${p.c} text-[10px] font-bold text-white ring-2 ring-[#f5f8fc] first:ml-0`}
                  >
                    {p.id}
                  </span>
                ))}
              </div>
              <span
                data-viewers-label
                className="ml-3 inline-flex items-center gap-1.5 text-xs text-secondary-dark/60"
              >
                <span className="size-1.5 animate-pulse rounded-full bg-emerald-500" />3 teammates
                on this order
              </span>
            </div>
            <span
              data-rx-link
              className="hidden items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.08em] text-secondary-dark/45 group-data-[compact=false]/stage:flex"
            >
              Rx −2.25 / −2.00 linked
            </span>
          </div>
        </div>
      </div>
    </Scene>
  );
}

/* ------------------------------------------------------------------ */
/*  05 · lab — verified complete, routed to the right lab              */
/* ------------------------------------------------------------------ */

const CHECKS = ['Lens & coating available', 'Thickness calculated', 'Bevel placement set'];
// The labs orders are routed to. #4821 goes to the first; the others each
// receive an order of their own, so the scene shows routing, not one delivery.
const LABS = [
  { name: 'Sunshine' },
  { name: 'Milligan', order: { id: '#4820', item: 'Aviator Classic · Single vision' } },
  { name: 'Mission Optics', order: { id: '#4819', item: 'Square Acetate · Blue light' } },
  { name: 'PROptics', order: { id: '#4818', item: 'Aviator Classic · Polarized' } },
];

export function LabScene() {
  return (
    <Scene name="lab">
      <div className="flex h-full gap-4 group-data-[compact=true]/stage:flex-col group-data-[compact=true]/stage:gap-3">
        <div className="flex w-[300px] shrink-0 flex-col group-data-[compact=true]/stage:w-full">
          <SceneLabel>Order verified</SceneLabel>
          <div data-anchor="job" className={`mt-3 h-[60px] rounded-xl ${WELL}`} />
          <ul className="mt-3 space-y-2">
            {CHECKS.map((c) => (
              <li
                key={c}
                data-check
                className="flex items-center gap-2 text-xs text-secondary-dark/70"
              >
                <Tick />
                {c}
              </li>
            ))}
          </ul>

          {/* the lens in section: what "thickness calculated" means */}
          <div
            className={`relative mt-auto h-[150px] overflow-hidden rounded-2xl group-data-[compact=true]/stage:hidden ${WELL}`}
          >
            <svg viewBox="0 0 300 150" fill="none" className="absolute inset-0 h-full w-full">
              <path
                data-xsec
                d="M44 34c70 22 142 22 212 0l4 84c-72-14-148-14-220 0Z"
                fill="#01a7ff"
                fillOpacity="0.1"
                stroke="#01a7ff"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
              <g data-xsec-dim stroke="#101828" strokeOpacity="0.45" strokeWidth="1">
                <path d="M150 51v55M145 51h10M145 106h10" />
                <path d="M30 34v84M25 34h10M25 118h10" />
              </g>
              <g
                data-xsec-dim
                fill="#101828"
                fillOpacity="0.7"
                fontSize="10"
                fontFamily="var(--font-mono), monospace"
              >
                <text x="158" y="82">
                  CT 1.8
                </text>
                <text x="40" y="136">
                  ET 4.1 mm
                </text>
              </g>
            </svg>
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-2 group-data-[compact=true]/stage:grid group-data-[compact=true]/stage:grid-cols-2 group-data-[compact=true]/stage:content-start">
          <div className="group-data-[compact=true]/stage:col-span-2">
            <SceneLabel>Your labs</SceneLabel>
          </div>
          {LABS.map((l, i) => (
            <div
              key={l.name}
              data-lab
              className={`relative rounded-xl border bg-white p-2 ${LINE}`}
            >
              {i === 0 && (
                <span
                  data-lab-ring
                  className="pointer-events-none absolute -inset-px rounded-xl ring-2 ring-primary-blue"
                />
              )}
              <div className="flex items-center gap-2 px-1">
                <Building2 className="size-3.5 shrink-0 text-secondary-dark/40" strokeWidth={2} />
                <span className="truncate text-xs font-semibold text-secondary-dark">{l.name}</span>
                <span className="ml-auto flex shrink-0 items-center gap-1.5 font-mono text-[9.5px] uppercase tracking-[0.08em] text-secondary-dark/45 group-data-[compact=true]/stage:hidden">
                  <span className="size-1.5 rounded-full bg-emerald-500" />
                  Connected
                </span>
              </div>
              {l.order ? (
                <div className={`mt-1.5 h-[44px] rounded-lg ${WELL}`}>
                  <div
                    data-routed
                    className={`flex h-full items-center gap-2 rounded-lg border bg-white px-2.5 ${LINE}`}
                  >
                    <span className="shrink-0 font-mono text-[11px] font-semibold text-secondary-dark/45">
                      {l.order.id}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[11px] text-secondary-dark/70">
                      {l.order.item}
                    </span>
                    <Tick className="!size-4" />
                  </div>
                </div>
              ) : (
                <div data-anchor="labslot" className={`mt-1.5 h-[44px] rounded-lg ${WELL}`} />
              )}
            </div>
          ))}
        </div>
      </div>
    </Scene>
  );
}

/* ------------------------------------------------------------------ */
/*  06 · edging — entered once, a barcode loads the job                */
/* ------------------------------------------------------------------ */

const STATIONS = ['Surfacing', 'Coating', 'Edging', 'Quality check'];
const DIAL = Array.from({ length: 36 }, (_, i) => i * 10);

export function EdgingScene() {
  return (
    <Scene name="edging">
      <div className="flex h-full flex-col">
        <SceneLabel>Production line</SceneLabel>

        <div className="relative mt-3">
          <div className="absolute left-[12.5%] right-[12.5%] top-[11px] h-[2px] rounded-full bg-secondary-dark/[0.08]">
            <div data-station-fill className="h-full w-full rounded-full bg-primary-blue" />
          </div>
          <div className="relative grid grid-cols-4">
            {STATIONS.map((s, i) => (
              <div key={s} className="flex flex-col items-center gap-1.5">
                <span className="relative grid size-6 place-items-center rounded-full border border-secondary-dark/10 bg-white font-mono text-[10px] text-secondary-dark/40">
                  {i + 1}
                  <span
                    data-station-on
                    className="absolute -inset-px grid place-items-center rounded-full bg-primary-blue text-white"
                  >
                    <Check className="size-3.5" strokeWidth={3} />
                  </span>
                </span>
                <span className="font-mono text-[9.5px] uppercase tracking-[0.06em] text-secondary-dark/55">
                  {s}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 flex min-h-0 flex-1 gap-4 group-data-[compact=true]/stage:flex-col group-data-[compact=true]/stage:gap-3">
          <div className="flex w-[250px] shrink-0 flex-col group-data-[compact=true]/stage:w-full">
            <div data-anchor="tray" className={`h-[112px] rounded-2xl ${WELL}`} />
            <p
              data-scan-note
              className="mt-2.5 flex items-center gap-2 text-xs text-secondary-dark/70"
            >
              <Tick />
              Barcode scanned — job loaded on the edger
            </p>
            <div className="mt-auto grid grid-cols-3 gap-1.5 group-data-[compact=true]/stage:hidden">
              {[
                ['A', '52.0'],
                ['B', '41.5'],
                ['DBL', '18.0'],
              ].map(([k, v]) => (
                <span key={k} data-measure className={`rounded-lg border px-2 py-1.5 ${LINE}`}>
                  <span className="block font-mono text-[8.5px] tracking-[0.1em] text-secondary-dark/40">
                    {k}
                  </span>
                  <span className="block font-mono text-[12px] text-secondary-dark">{v}</span>
                </span>
              ))}
            </div>
          </div>

          <div className={`relative min-h-0 flex-1 overflow-hidden rounded-2xl ${WELL}`}>
            <svg viewBox="0 0 300 200" fill="none" className="absolute inset-0 h-full w-full">
              <g transform="translate(150 100)">
                {/* the tracer's degree dial */}
                <g stroke="#101828" strokeOpacity="0.2" strokeWidth="1">
                  {DIAL.map((a) => (
                    <path
                      key={a}
                      d={a % 30 ? 'M0-88V-84' : 'M0-88V-80'}
                      transform={`rotate(${a})`}
                    />
                  ))}
                </g>
                {/* the uncut lens blank */}
                <circle r="76" fill="#01a7ff" fillOpacity="0.06" />
                <circle r="76" stroke="#01a7ff" strokeOpacity="0.35" strokeDasharray="3 5" />
                <path d="M-76 0H76" stroke="#101828" strokeOpacity="0.18" strokeDasharray="2 4" />
                {/* the wheel, sweeping once round as the shape is cut */}
                <g data-wheel>
                  <path d="M0 0H-76" stroke="#01a7ff" strokeOpacity="0.5" strokeWidth="1" />
                  <circle cx="-76" r="5" fill="#fff" stroke="#01a7ff" strokeWidth="2" />
                </g>
                {/* the frame shape the edger cuts it to */}
                <path
                  data-lens-path
                  d="M-58-17c0-22 20-34 58-34s62 12 62 36c0 34-22 60-62 60s-58-24-58-62Z"
                  stroke="#01a7ff"
                  strokeWidth="3"
                  pathLength="1000"
                  strokeDasharray="1000"
                />
                <circle r="2.5" fill="#01a7ff" />
              </g>
            </svg>
            <span className="absolute left-3 top-3 font-mono text-[10px] uppercase tracking-[0.12em] text-secondary-dark/50">
              Edger 2
            </span>
            <span className="absolute right-3 top-3 font-mono text-[10px] text-secondary-dark/50">
              180°
            </span>
            <span
              data-lens-label
              className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-ink px-2.5 py-1 text-[11px] font-medium text-white"
            >
              <span className="size-1.5 rounded-full bg-emerald-400" />
              Shape cut
            </span>
          </div>
        </div>
      </div>
    </Scene>
  );
}

/* ------------------------------------------------------------------ */
/*  07 · ship — out the door, tracked to the customer                  */
/* ------------------------------------------------------------------ */

const DEST = SHIP_ROUTES[0];
const TRACK = ['Label created', 'In transit', 'Delivered'];
const PARCEL_W = 104;
const PARCEL_H = 30;

export function ShipScene() {
  return (
    <Scene name="ship">
      <div className="flex h-full flex-col gap-3">
        <div
          className={`flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-2xl group-data-[compact=true]/stage:flex-none ${WELL}`}
        >
          <div className="relative aspect-[900/520] h-full group-data-[compact=true]/stage:h-auto group-data-[compact=true]/stage:w-full">
            <svg
              viewBox={`0 0 ${MAP_W} ${MAP_H}`}
              preserveAspectRatio="none"
              fill="none"
              className="absolute inset-0 h-full w-full"
            >
              {US_STATE_PATHS.map((d, i) => (
                <path
                  key={i}
                  d={d}
                  fill="#e6eef9"
                  stroke="#c6d4e8"
                  strokeWidth="0.8"
                  strokeLinejoin="round"
                />
              ))}
              {SHIP_ROUTES.map((r, i) => (
                <g key={r.name} data-route>
                  <path
                    d={r.d}
                    stroke={i ? '#101828' : '#01a7ff'}
                    strokeOpacity={i ? 0.22 : 0.4}
                    strokeWidth="2"
                    strokeDasharray="4 7"
                    strokeLinecap="round"
                  />
                  <circle
                    cx={r.end[0]}
                    cy={r.end[1]}
                    r="5.5"
                    fill={i ? '#94a3b8' : '#01a7ff'}
                    stroke="#fff"
                    strokeWidth="1.5"
                  />
                </g>
              ))}
              {/* the leg this parcel flies, drawn as it goes */}
              <path
                data-flight
                d={DEST.d}
                stroke="#01a7ff"
                strokeWidth="3.5"
                strokeLinecap="round"
                pathLength="1000"
                strokeDasharray="1000"
              />
              <circle cx={HUB[0]} cy={HUB[1]} r="8" fill="#101828" stroke="#fff" strokeWidth="2" />
            </svg>

            {/* Slots are placed with margins, not transforms: the timeline
                measures offsetLeft/Top, which a transform would not move. */}
            <div
              data-anchor="hub"
              className="absolute"
              style={{
                ...pct(HUB),
                width: PARCEL_W,
                height: PARCEL_H,
                marginLeft: -PARCEL_W / 2,
                marginTop: -PARCEL_H - 12,
              }}
            />
            <div
              data-anchor="dest"
              className="absolute"
              style={{
                ...pct(DEST.end),
                width: PARCEL_W,
                height: PARCEL_H,
                marginLeft: -PARCEL_W + 10,
                marginTop: -PARCEL_H - 12,
              }}
            />

            <span className="absolute bottom-3 left-3 font-mono text-[10px] uppercase tracking-[0.12em] text-secondary-dark/50">
              Lab → customer
            </span>
          </div>
        </div>

        <div className="grid shrink-0 grid-cols-3 gap-2 group-data-[compact=true]/stage:grid-cols-1">
          {TRACK.map((t, i) => (
            <div
              key={t}
              data-track
              className={`flex h-9 items-center gap-2 rounded-xl border px-3 text-xs font-medium text-secondary-dark ${LINE}`}
            >
              <Tick
                className={
                  i === TRACK.length - 1 ? '' : '!bg-secondary-dark/15 !text-secondary-dark/60'
                }
              />
              {t}
            </div>
          ))}
        </div>
      </div>
    </Scene>
  );
}

/* ------------------------------------------------------------------ */
/*  The token's faces — one per slot it is laid over                   */
/* ------------------------------------------------------------------ */

const FACE = 'absolute inset-0 bg-white';
const HIDDEN = 'invisible opacity-0';
const INK = 'text-[#1d3a8a]';

/** A value in the doctor's hand, with the box the AI draws round it. */
function Written({ children, className = '' }) {
  return (
    <span className={`relative inline-block px-1 ${className}`}>
      {children}
      <span
        data-bbox
        className="absolute -inset-x-0 -inset-y-0.5 rounded-[3px] border border-primary-blue bg-primary-blue/10"
      />
    </span>
  );
}

export function TokenFaces() {
  return (
    <>
      <div data-face="frame" className={FACE}>
        <FrameCard {...FRAMES[CHOSEN_FRAME]} chosen>
          <span
            data-added
            className="absolute right-1.5 top-1.5 inline-flex items-center gap-0.5 rounded-full bg-primary-blue px-1.5 py-0.5 text-[9px] font-semibold text-white shadow"
          >
            <Check className="size-2.5" strokeWidth={3} />
            Added
          </span>
          <span data-cursor className="absolute bottom-2 right-2 text-secondary-dark">
            <MousePointer2 className="size-4 fill-secondary-dark" />
          </span>
        </FrameCard>
      </div>

      <div data-face="order" className={`${FACE} ${HIDDEN} flex items-center gap-2.5 px-3`}>
        <OrderChip />
        <span className="min-w-0 flex-1 truncate text-xs font-medium text-secondary-dark">
          Round Metal · Progressive
        </span>
        <span className="shrink-0 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-primary-blue">
          New
        </span>
      </div>

      <div
        data-face="rx"
        className={`${FACE} ${HIDDEN} flex flex-col bg-[#fffdf8] p-4 group-data-[compact=true]/stage:p-3`}
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="font-display text-[15px] font-bold leading-none text-secondary-dark">
              Rx
            </div>
            <div className="mt-1 font-mono text-[8.5px] uppercase tracking-[0.12em] text-secondary-dark/45 group-data-[compact=true]/stage:hidden">
              Eyeglass prescription
            </div>
          </div>
          <OrderChip />
        </div>

        <div className="mt-3 flex items-baseline gap-2 border-b border-dashed border-secondary-dark/20 pb-1 group-data-[compact=true]/stage:mt-1.5">
          <span className="font-mono text-[8.5px] uppercase tracking-[0.1em] text-secondary-dark/45">
            Patient
          </span>
          <span className={`font-hand text-[19px] leading-none ${INK}`}>Emma Collins</span>
        </div>

        <div className="mt-3 grid grid-cols-[22px_repeat(4,1fr)] items-center gap-y-1.5 group-data-[compact=true]/stage:mt-1.5 group-data-[compact=true]/stage:gap-y-0.5">
          <span />
          {RX_COLS.map((c) => (
            <span
              key={c}
              className="text-center font-mono text-[8.5px] tracking-[0.1em] text-secondary-dark/45"
            >
              {c}
            </span>
          ))}
          {RX_ROWS.map(([eye, ...vals]) => (
            <div key={eye} className="contents">
              <span className="font-mono text-[10px] font-semibold text-secondary-dark/70">
                {eye}
              </span>
              {vals.map((v, i) => (
                <span key={i} className="text-center">
                  <Written className={`font-hand text-[20px] leading-none ${INK}`}>{v}</Written>
                </span>
              ))}
            </div>
          ))}
        </div>

        <div className="mt-3 flex items-baseline gap-2 group-data-[compact=true]/stage:hidden">
          <span className="font-mono text-[8.5px] uppercase tracking-[0.1em] text-secondary-dark/45">
            PD
          </span>
          {/* left blank by the doctor, as it usually is */}
          <span className="w-10 self-end border-b border-dashed border-secondary-dark/30" />
          <span className="ml-3 font-mono text-[8.5px] uppercase tracking-[0.1em] text-secondary-dark/45">
            Lens
          </span>
          <Written className={`font-hand text-[19px] leading-none ${INK}`}>progressive</Written>
        </div>

        <svg
          viewBox="0 0 120 34"
          fill="none"
          className={`mt-auto h-8 w-[120px] group-data-[compact=true]/stage:hidden ${INK}`}
        >
          <path
            d="M3 24c10-18 15-20 17-8s8 13 15-2 10-9 12 3 10 8 17-5 12-5 15 5 15 3 38-12"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>

        <div
          data-scan
          className="pointer-events-none absolute inset-x-0 h-16"
          style={{ top: '-30%' }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-primary-blue/0 to-primary-blue/20" />
          <div className="absolute inset-x-0 bottom-0 h-px bg-primary-blue shadow-[0_0_14px_2px_rgba(1,167,255,0.8)]" />
        </div>
      </div>

      <div data-face="fit" className={`${FACE} ${HIDDEN} flex items-center gap-2.5 px-3`}>
        <span className="grid h-9 w-12 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-[#eef6ff] to-[#f8fbff] text-secondary-dark">
          <FrameArt shape="round" className="w-9" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <OrderChip />
            <span className="truncate text-xs font-semibold text-secondary-dark">Round Metal</span>
          </span>
          <span className="mt-0.5 block truncate font-mono text-[10px] text-secondary-dark/50">
            Virtual try-on
          </span>
        </span>
        <span data-fit-done>
          <Tick />
        </span>
      </div>

      <div data-face="record" className={`${FACE} ${HIDDEN} flex items-center gap-2.5 px-3`}>
        <OrderChip />
        <span className="min-w-0 flex-1 truncate text-xs font-medium text-secondary-dark">
          Round Metal · Progressive
        </span>
        <span className="relative grid shrink-0 justify-items-end font-mono text-[10px] font-semibold uppercase tracking-[0.08em]">
          <span data-rec-status="new" className="text-primary-blue [grid-area:1/1]">
            New
          </span>
          <span data-rec-status="done" className={`${HIDDEN} text-emerald-600 [grid-area:1/1]`}>
            Processed
          </span>
        </span>
      </div>

      <div data-face="job" className={`${FACE} ${HIDDEN} flex items-center gap-2.5 px-3`}>
        <span className="grid h-9 w-12 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-[#eef6ff] to-[#f8fbff] text-secondary-dark">
          <FrameArt shape="round" className="w-9" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <OrderChip />
            <span className="truncate text-xs font-semibold text-secondary-dark">Round Metal</span>
          </span>
          <span className="mt-0.5 block truncate font-mono text-[10px] text-secondary-dark/50">
            Progressive 1.67 · Blue light
          </span>
        </span>
        <Tick />
      </div>

      <div data-face="tray" className={`${FACE} ${HIDDEN} p-3`}>
        <div className="flex items-center justify-between">
          <OrderChip />
          <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-secondary-dark/45">
            Job tray 17
          </span>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <span className="size-12 shrink-0 rounded-full border-2 border-primary-blue/40 bg-primary-blue/[0.06]" />
          <span className="size-12 shrink-0 rounded-full border-2 border-primary-blue/40 bg-primary-blue/[0.06]" />
          <span
            className={`relative h-12 min-w-0 flex-1 overflow-hidden rounded-md border bg-white ${LINE}`}
          >
            <span className="absolute inset-1.5 bg-[repeating-linear-gradient(90deg,#101828_0,#101828_2px,transparent_2px,transparent_4px,#101828_4px,#101828_5px,transparent_5px,transparent_9px)]" />
            <span
              data-beam
              className="absolute inset-y-0 left-0 w-0.5 bg-rose-500 shadow-[0_0_12px_3px_rgba(244,63,94,0.6)]"
            />
          </span>
        </div>
      </div>

      <div
        data-face="parcel"
        className={`absolute inset-0 ${HIDDEN} flex items-center justify-center gap-1.5 bg-ink text-white`}
      >
        <Package className="size-3.5" strokeWidth={2.25} />
        <span className="font-mono text-[11px] font-semibold">{ORDER_ID}</span>
        <span
          data-parcel-done
          className="absolute inset-0 flex items-center justify-center gap-1 bg-emerald-500 text-[11px] font-semibold text-white"
        >
          <Check className="size-3.5" strokeWidth={3} />
          Delivered
        </span>
      </div>
    </>
  );
}
